import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Progress } from "./ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Mic,
  Upload,
  Trash2,
  Play,
  Pause,
  Loader2,
  CheckCircle,
  AlertCircle,
  Volume2,
  X,
  Plus,
  User,
  Square,
  RotateCcw,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { voiceAPI } from "../lib/api";

// Guided recording prompts - designed to capture voice variety
const RECORDING_PROMPTS = [
  {
    id: 1,
    title: "Introduction",
    text: "Hi there! My name is [Your Name], and I'm excited to create content that connects with my audience. I believe in sharing authentic stories that inspire and entertain.",
    tip: "Speak naturally, as if introducing yourself to a new friend."
  },
  {
    id: 2,
    title: "Enthusiasm",
    text: "Oh wow, this is absolutely incredible! I can't believe how amazing this turned out. You're going to love what I'm about to show you - it's seriously the best thing ever!",
    tip: "Show excitement and energy in your voice."
  },
  {
    id: 3,
    title: "Informative",
    text: "Let me walk you through the key points. First, we need to understand the basics. Second, we'll explore the main features. And finally, I'll share some pro tips that will save you time.",
    tip: "Use a clear, educational tone like you're teaching something."
  },
  {
    id: 4,
    title: "Storytelling",
    text: "So there I was, standing at the crossroads, wondering which path to take. The journey hadn't been easy, but something told me that the best was yet to come. And you know what? I was right.",
    tip: "Be expressive and draw the listener into the story."
  },
  {
    id: 5,
    title: "Call to Action",
    text: "If you found this helpful, make sure to like and subscribe for more content like this. Drop a comment below and let me know what you think. I read every single one!",
    tip: "Sound warm and inviting, encouraging engagement."
  }
];

export function VoiceCloneManager() {
  const [clonedVoices, setClonedVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [voiceName, setVoiceName] = useState("");
  const [voiceDescription, setVoiceDescription] = useState("");
  const [playingPreview, setPlayingPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Recording state
  const [inputMethod, setInputMethod] = useState("record"); // "record" or "upload"
  const [isRecording, setIsRecording] = useState(false);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [recordings, setRecordings] = useState([]); // Array of {promptId, blob, url}
  const [recordingTime, setRecordingTime] = useState(0);
  const [playingRecording, setPlayingRecording] = useState(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const playbackAudioRef = useRef(null);

  // Fetch user's cloned voices
  const fetchVoices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await voiceAPI.getMyVoices();
      setClonedVoices(response.data.voices || []);
    } catch (error) {
      console.error("Error fetching voices:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load voices on mount
  useState(() => {
    fetchVoices();
  }, []);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (f) => f.type.startsWith("audio/") || f.name.match(/\.(mp3|wav|m4a|ogg|webm)$/i)
    );
    
    if (validFiles.length !== files.length) {
      toast.error("Some files were not audio files and were skipped");
    }
    
    if (validFiles.length + selectedFiles.length > 25) {
      toast.error("Maximum 25 audio files allowed");
      return;
    }
    
    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  // Remove selected file
  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Clone voice
  const handleCloneVoice = async () => {
    if (!voiceName.trim()) {
      toast.error("Please enter a name for your voice");
      return;
    }
    
    // Check for either uploaded files or recordings
    const hasFiles = selectedFiles.length > 0;
    const hasRecordings = recordings.length > 0;
    
    if (!hasFiles && !hasRecordings) {
      toast.error("Please record or upload at least one audio sample");
      return;
    }
    
    setUploading(true);
    setUploadProgress(10);
    
    try {
      const formData = new FormData();
      formData.append("name", voiceName.trim());
      formData.append("description", voiceDescription.trim() || "");
      
      // Add uploaded files
      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });
      
      // Add recordings as files
      recordings.forEach((rec, index) => {
        const file = new File([rec.blob], `recording_${index + 1}.webm`, { type: 'audio/webm' });
        formData.append("files", file);
      });
      
      setUploadProgress(30);
      
      const response = await voiceAPI.cloneVoice(formData);
      
      setUploadProgress(100);
      
      // Axios returns response.data for the actual API response
      const result = response.data;
      
      if (result.success) {
        toast.success(`Voice "${voiceName}" cloned successfully!`);
        setClonedVoices([...clonedVoices, result.voice]);
        setShowUploadForm(false);
        setSelectedFiles([]);
        setRecordings([]);
        setVoiceName("");
        setVoiceDescription("");
        setCurrentPromptIndex(0);
      } else {
        throw new Error(result.error || "Voice cloning failed");
      }
    } catch (error) {
      console.error("Clone error:", error);
      const statusCode = error.response?.status;
      // Extract error message from axios error response if available
      let errorMessage = error.response?.data?.detail || error.response?.data?.error || error.message || "Failed to clone voice";
      
      // Provide user-friendly message for payment/quota issues
      if (statusCode === 402) {
        errorMessage = "ElevenLabs credits exhausted. Please add credits to your ElevenLabs account at elevenlabs.io to continue using voice cloning.";
      }
      
      toast.error(errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      recordingChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordingChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const currentPrompt = RECORDING_PROMPTS[currentPromptIndex];
        
        setRecordings(prev => [
          ...prev.filter(r => r.promptId !== currentPrompt.id),
          { promptId: currentPrompt.id, blob, url, promptTitle: currentPrompt.title }
        ]);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error("Recording error:", error);
      toast.error("Could not access microphone. Please allow microphone access.");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  // Play recording
  const playRecording = (recording) => {
    if (playingRecording === recording.promptId) {
      if (playbackAudioRef.current) {
        playbackAudioRef.current.pause();
        playbackAudioRef.current = null;
      }
      setPlayingRecording(null);
      return;
    }
    
    if (playbackAudioRef.current) {
      playbackAudioRef.current.pause();
    }
    
    const audio = new Audio(recording.url);
    playbackAudioRef.current = audio;
    audio.onended = () => setPlayingRecording(null);
    audio.play();
    setPlayingRecording(recording.promptId);
  };

  // Delete recording
  const deleteRecording = (promptId) => {
    const recording = recordings.find(r => r.promptId === promptId);
    if (recording) {
      URL.revokeObjectURL(recording.url);
    }
    setRecordings(prev => prev.filter(r => r.promptId !== promptId));
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current prompt
  const currentPrompt = RECORDING_PROMPTS[currentPromptIndex];
  const hasRecordingForCurrentPrompt = recordings.some(r => r.promptId === currentPrompt.id);

  // Delete voice
  const handleDeleteVoice = async (voiceId, voiceName) => {
    if (!confirm(`Delete voice "${voiceName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await voiceAPI.deleteVoice(voiceId);
      setClonedVoices(clonedVoices.filter((v) => v.voice_id !== voiceId));
      toast.success("Voice deleted");
    } catch (error) {
      toast.error("Failed to delete voice");
    }
  };

  // Preview voice
  const handlePreviewVoice = async (voiceId) => {
    if (playingPreview === voiceId) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingPreview(null);
      return;
    }
    
    setPreviewLoading(voiceId);
    
    try {
      const formData = new FormData();
      formData.append("voice_id", voiceId);
      formData.append("text", "Hello! This is a preview of my cloned voice. It sounds just like me!");
      
      const response = await voiceAPI.previewVoice(formData);
      
      if (response.success && response.audio_url) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        
        const audio = new Audio(response.audio_url);
        audioRef.current = audio;
        
        audio.onended = () => {
          setPlayingPreview(null);
        };
        
        audio.play();
        setPlayingPreview(voiceId);
      }
    } catch (error) {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewLoading(null);
    }
  };

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              My Voices
            </CardTitle>
            <CardDescription>
              Clone your voice to use as narrator in your videos
            </CardDescription>
          </div>
          <Button
            onClick={() => setShowUploadForm(!showUploadForm)}
            variant={showUploadForm ? "outline" : "default"}
            size="sm"
            data-testid="add-voice-btn"
          >
            {showUploadForm ? (
              <>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Add My Voice
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Upload Form */}
        <AnimatePresence>
          {showUploadForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="bg-secondary/30 rounded-lg p-6 space-y-4 border border-border/50">
                <div className="space-y-2">
                  <Label className="text-white">Voice Name</Label>
                  <Input
                    value={voiceName}
                    onChange={(e) => setVoiceName(e.target.value)}
                    placeholder="e.g., My Voice, Studio Voice"
                    className="bg-secondary/50 border-border/50"
                    data-testid="voice-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Description (optional)</Label>
                  <Input
                    value={voiceDescription}
                    onChange={(e) => setVoiceDescription(e.target.value)}
                    placeholder="e.g., Energetic, professional tone"
                    className="bg-secondary/50 border-border/50"
                  />
                </div>
                
                {/* Audio Input Method - Record or Upload */}
                <div className="space-y-4">
                  <Label className="text-white">Voice Samples</Label>
                  
                  <Tabs value={inputMethod} onValueChange={setInputMethod} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
                      <TabsTrigger value="record" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                        <Mic className="w-4 h-4 mr-2" />
                        Record Voice
                      </TabsTrigger>
                      <TabsTrigger value="upload" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Files
                      </TabsTrigger>
                    </TabsList>
                    
                    {/* Record Tab */}
                    <TabsContent value="record" className="mt-4 space-y-4">
                      <div className="text-sm text-muted-foreground">
                        Read the prompts below while recording. Complete at least 3 for best results.
                      </div>
                      
                      {/* Prompt Card */}
                      <div className="bg-secondary/50 rounded-lg p-4 border border-border/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-primary font-medium">
                            Prompt {currentPromptIndex + 1} of {RECORDING_PROMPTS.length}
                          </span>
                          {hasRecordingForCurrentPrompt && (
                            <span className="text-xs text-green-400 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Recorded
                            </span>
                          )}
                        </div>
                        <h4 className="text-white font-medium mb-2">{currentPrompt.title}</h4>
                        <p className="text-sm text-white bg-background/50 rounded p-3 mb-3 leading-relaxed">
                          "{currentPrompt.text}"
                        </p>
                        <p className="text-xs text-muted-foreground italic">
                          💡 {currentPrompt.tip}
                        </p>
                      </div>
                      
                      {/* Recording Controls */}
                      <div className="flex items-center justify-center gap-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPromptIndex(Math.max(0, currentPromptIndex - 1))}
                          disabled={currentPromptIndex === 0 || isRecording}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        
                        {isRecording ? (
                          <Button
                            onClick={stopRecording}
                            variant="destructive"
                            size="lg"
                            className="w-32 h-12"
                          >
                            <Square className="w-5 h-5 mr-2" />
                            {formatTime(recordingTime)}
                          </Button>
                        ) : (
                          <Button
                            onClick={startRecording}
                            variant="default"
                            size="lg"
                            className="w-32 h-12 bg-red-500 hover:bg-red-600"
                            data-testid="start-recording-btn"
                          >
                            <Mic className="w-5 h-5 mr-2" />
                            Record
                          </Button>
                        )}
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPromptIndex(Math.min(RECORDING_PROMPTS.length - 1, currentPromptIndex + 1))}
                          disabled={currentPromptIndex === RECORDING_PROMPTS.length - 1 || isRecording}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Recorded Samples */}
                      {recordings.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-sm text-white font-medium">
                            Recorded samples ({recordings.length}/{RECORDING_PROMPTS.length}):
                          </div>
                          <div className="space-y-1">
                            {recordings.map((rec) => (
                              <div
                                key={rec.promptId}
                                className="flex items-center justify-between bg-secondary/50 rounded px-3 py-2 text-sm"
                              >
                                <span className="text-muted-foreground flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                  {rec.promptTitle}
                                </span>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => playRecording(rec)}
                                    className="h-7 w-7 p-0"
                                  >
                                    {playingRecording === rec.promptId ? (
                                      <Pause className="w-4 h-4" />
                                    ) : (
                                      <Play className="w-4 h-4" />
                                    )}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteRecording(rec.promptId)}
                                    className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Upload Tab */}
                    <TabsContent value="upload" className="mt-4 space-y-4">
                      <div className="text-sm text-muted-foreground">
                        Upload 1-25 audio files. For best results, use clear recordings of 1-3 minutes each.
                      </div>
                      
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="audio/*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      >
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Click to upload audio files
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          MP3, WAV, M4A, OGG supported
                        </p>
                      </div>
                      
                      {/* Selected files list */}
                      {selectedFiles.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-sm text-white font-medium">
                            Selected files ({selectedFiles.length}/25):
                          </div>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {selectedFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-secondary/50 rounded px-3 py-2 text-sm"
                              >
                                <span className="truncate text-muted-foreground">
                                  {file.name}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFile(index)}
                                  className="h-6 w-6 p-0"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </div>
                
                {/* Upload progress */}
                {uploading && (
                  <div className="space-y-2">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-sm text-muted-foreground text-center">
                      {uploadProgress < 30 ? "Uploading files..." : 
                       uploadProgress < 100 ? "Cloning voice..." : "Complete!"}
                    </p>
                  </div>
                )}
                
                <Button
                  onClick={handleCloneVoice}
                  disabled={uploading || !voiceName.trim() || (selectedFiles.length === 0 && recordings.length === 0)}
                  className="w-full"
                  data-testid="clone-voice-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cloning Voice...
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-2" />
                      Clone My Voice
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Cloned Voices List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : clonedVoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No cloned voices yet</p>
            <p className="text-sm mt-1">
              Add your voice to use it as a narrator in your videos
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {clonedVoices.map((voice) => (
              <motion.div
                key={voice.voice_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between bg-secondary/30 rounded-lg p-4 border border-border/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Mic className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium text-white">{voice.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {voice.description || "Custom cloned voice"}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Preview button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewVoice(voice.voice_id)}
                    disabled={previewLoading === voice.voice_id}
                    data-testid={`preview-voice-${voice.voice_id}`}
                  >
                    {previewLoading === voice.voice_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : playingPreview === voice.voice_id ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </Button>
                  
                  {/* Delete button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteVoice(voice.voice_id, voice.name)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-voice-${voice.voice_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Tips */}
        <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
          <h4 className="font-medium text-white mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-primary" />
            Tips for Best Voice Clone
          </h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Use clear, high-quality recordings with minimal background noise</li>
            <li>• Record 1-3 minutes of natural speech per sample</li>
            <li>• Include varied sentences and emotions for better results</li>
            <li>• Multiple samples improve voice accuracy</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
